import React, { createContext, useContext, useState, useCallback, useEffect } from "react"
import useAuthAxios from "../hooks/useAuthAxios"
import { formatDateForApi } from "../utils/dateUtils"

const CalendarContext = createContext(null)

// Static method to clear calendar data
let clearCalendarData = () => {}

// Helper to get default date range
const getDefaultDateRange = () => {
  const today = new Date()
  const from = new Date(today)
  from.setDate(today.getDate() - 3)
  from.setHours(0, 0, 0, 0)

  const to = new Date(today)
  to.setDate(today.getDate() + 3)
  to.setHours(23, 59, 59, 999)
  return { from, to }
}

export const CalendarProvider = ({ children }) => {
  const [inspections, setInspections] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [dateRange, setDateRange] = useState(getDefaultDateRange())
  const { get } = useAuthAxios()

  const clearInspections = useCallback(() => {
    setInspections([])
    setError(null)
    setDateRange(getDefaultDateRange())
  }, [])

  const logoutCleanup = useCallback(() => {
    clearInspections()
    setIsLoading(true)
  }, [clearInspections])

  // Update the static method when the component mounts
  useEffect(() => {
    clearCalendarData = logoutCleanup
    return () => {
      clearCalendarData = () => {}
    }
  }, [logoutCleanup])

  const fetchInspections = useCallback(
    async (force = false, dateFromArg = null, dateToArg = null) => {
      // Determine effective dates: Use arguments if provided, otherwise use context state
      const effectiveDateFrom = dateFromArg || dateRange.from
      const effectiveDateTo = dateToArg || dateRange.to

      // Skip refetch if data exists, not forcing, and dates haven't changed (unless args were passed)
      if (
        inspections.length > 0 &&
        !force &&
        !dateFromArg &&
        !dateToArg &&
        effectiveDateFrom === dateRange.from &&
        effectiveDateTo === dateRange.to
      ) {
        return
      }

      setIsLoading(true)
      setError(null)
      try {
        // Build params object only with effective dates
        const params = {}
        if (effectiveDateFrom) {
          params.ExpertAssignmentDateFrom = formatDateForApi(effectiveDateFrom)
        }
        if (effectiveDateTo) {
          params.ExpertAssignmentDateTimeTo = formatDateForApi(effectiveDateTo)
        }

        // Use silentFail for initial/background load, but not for manual refresh (force=true)
        const response = await get("/inspections", { params }, !force)
        setInspections(response.data ?? [])
      } catch (err) {
        // Only set error if it's not a handled error or if it's a manual refresh
        if (!err.handled || force) {
          setError(err)
          console.error("Calendar fetch error:", err) // Log error for debugging
        }
      } finally {
        setIsLoading(false)
      }
    },
    [get, inspections.length, dateRange.from, dateRange.to]
  )

  // Unforced fetch inspections whenever the date range changes
  useEffect(() => {
    fetchInspections()
  }, [dateRange]) // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = useCallback(
    (dateFrom = null, dateTo = null) => {
      return fetchInspections(true, dateFrom, dateTo)
    },
    [fetchInspections]
  )

  const value = {
    inspections,
    isLoading,
    error,
    dateRange,
    setDateRange,
    fetchInspections,
    refresh,
    clearInspections,
    logoutCleanup
  }

  return <CalendarContext.Provider value={value}>{children}</CalendarContext.Provider>
}

export const useCalendar = () => {
  const context = useContext(CalendarContext)
  if (!context) {
    throw new Error("useCalendar must be used within a CalendarProvider")
  }
  return context
}

// Export the static method
export const clearCalendar = () => clearCalendarData()
